// src/pages/VentesJour.jsx
import React, { useEffect, useState } from "react";
import { Button, Card, Col, Row, Spinner, Alert, Table, Form, Badge } from "react-bootstrap";
import { Link } from "react-router-dom";
import ReactApexChart from "react-apexcharts";
import Footer from "../layouts/Footer";
import Header from "../layouts/Header";
import { formatCurrency, formatNumber, formatDate } from "../utils/formatters";
import { API_BASE_URL } from "../constants/api";
import { COLORS } from "../constants/colors";

export default function VentesJour() {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split('T')[0]);
    const [activeTab, setActiveTab] = useState('factures');
    const [autoRefresh, setAutoRefresh] = useState(true);

    useEffect(() => {
        fetchVentesJour();
        let interval;
        if (autoRefresh) interval = setInterval(fetchVentesJour, 60000);
        return () => clearInterval(interval);
    }, [selectedDate, autoRefresh]);

    /* ============================================================
       FETCH
       ============================================================ */
    const fetchVentesJour = async () => {
        try {
            setLoading(true);
            const token = localStorage.getItem("token");
            const response = await fetch(
                `${API_BASE_URL}/ventes_jour.php?date=${selectedDate}`,
                { headers: { 'Authorization': `Bearer ${token}` } }
            );
            if (!response.ok) throw new Error("Erreur de chargement des données");
            const result = await response.json();
            setData(result);
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    const handleDateChange = (e) => setSelectedDate(e.target.value);
    const handleRefresh = () => fetchVentesJour();

    /* ============================================================
       HELPERS
       ============================================================ */
    const getStatusConfig = (statut) => {
        const configs = {
            'emise':          { gradient: 'linear-gradient(135deg, #f59e0b, #d97706)', text: 'Émise',     icon: 'ri-time-line' },
            'payee':          { gradient: 'linear-gradient(135deg, #10b981, #059669)', text: 'Payée',     icon: 'ri-checkbox-circle-line' },
            'payee_partielle':{ gradient: 'linear-gradient(135deg, #3b82f6, #2563eb)', text: 'Partielle', icon: 'ri-indeterminate-circle-line' },
            'impayee':        { gradient: 'linear-gradient(135deg, #ef4444, #dc2626)', text: 'Impayée',   icon: 'ri-close-circle-line' },
            'annulee':        { gradient: 'linear-gradient(135deg, #64748b, #475569)', text: 'Annulée',   icon: 'ri-forbid-line' }
        };
        return configs[statut] || { gradient: 'linear-gradient(135deg, #64748b, #475569)', text: statut, icon: 'ri-question-line' };
    };

    const getStatusBadge = (statut) => {
        const config = getStatusConfig(statut);
        return (
            <span className="status-badge-charte" style={{ background: config.gradient }}>
                <i className={config.icon}></i>
                {config.text}
            </span>
        );
    };

    const getModePaiementLabel = (mode) => {
        const labels = {
            'especes': 'Espèces',
            'cheque': 'Chèque',
            'virement': 'Virement',
            'carte_bancaire': 'Carte bancaire',
            'traite': 'Traite'
        };
        return labels[mode] || mode;
    };

    /* ============================================================
       GRAPHIQUES
       ============================================================ */
    const chartColors = {
        primary: COLORS.primary,
        secondary: COLORS.secondary,
        success: COLORS.success,
        warning: COLORS.warning,
        danger: COLORS.danger,
        info: COLORS.info,
        purple: COLORS.purple || '#8b5cf6',
        cyan: COLORS.cyan || '#06b6d4'
    };

    const seriesVentesHeure = [{
        name: 'Montant des ventes',
        data: data?.ventes_par_heure?.map(item => item.montant_total) || []
    }];

    const optionsVentesHeure = {
        chart: { type: 'area', height: 350, toolbar: { show: false }, zoom: { enabled: false }, fontFamily: 'Inter, system-ui, sans-serif' },
        colors: [chartColors.primary],
        fill: { type: 'gradient', gradient: { shadeIntensity: 1, opacityFrom: 0.7, opacityTo: 0.1 } },
        stroke: { curve: 'smooth', width: 2 },
        grid: { borderColor: '#e9ecef', strokeDashArray: 5 },
        xaxis: { categories: data?.ventes_par_heure?.map(item => `${item.heure}h`) || [], labels: { style: { fontSize: '11px', fontWeight: 500 } } },
        yaxis: { labels: { formatter: (value) => formatCurrency(value), style: { fontSize: '11px' } } },
        tooltip: { y: { formatter: (value) => formatCurrency(value) } }
    };

    const seriesRepartitionPaiements = data?.moyens_paiement?.map(item => item.total) || [];

    const donutOptions = {
        chart: { type: 'donut', height: 280, fontFamily: 'Inter, system-ui, sans-serif', toolbar: { show: false } },
        labels: data?.moyens_paiement?.map(item => getModePaiementLabel(item.mode_reglement)) || [],
        colors: [chartColors.primary, chartColors.cyan, chartColors.success, chartColors.warning, chartColors.danger],
        legend: { position: 'bottom', fontSize: '11px', offsetY: 5, markers: { radius: 8 } },
        tooltip: { y: { formatter: (value) => formatCurrency(value) } },
        plotOptions: {
            pie: {
                donut: {
                    size: '65%',
                    labels: {
                        show: true,
                        total: {
                            show: true,
                            label: 'Total',
                            fontSize: '14px',
                            fontWeight: 600,
                            color: COLORS.primaryDark,
                            formatter: () => formatCurrency(data?.moyens_paiement?.reduce((acc, item) => acc + item.total, 0) || 0)
                        }
                    }
                }
            }
        },
        dataLabels: { style: { fontSize: '11px', fontWeight: 500 } },
        stroke: { show: false }
    };

    /* ============================================================
       LOADING
       ============================================================ */
    if (loading && !data) {
        return (
            <>
                <Header />
                <div className="main main-app p-3 p-lg-4 d-flex justify-content-center align-items-center"
                    style={{ minHeight: 'calc(100vh - 140px)', background: '#f8fafc' }}>
                    <div className="text-center">
                        <div className="loading-spinner-charte">
                            <div className="loading-ring-charte"></div>
                            <i className="ri-shopping-bag-3-line loading-icon-charte"></i>
                        </div>
                        <h5 className="fw-bold mt-4 mb-2" style={{ color: COLORS.primary }}>
                            Chargement des ventes
                        </h5>
                        <p className="text-muted small">Récupération des données en cours...</p>
                    </div>
                </div>
                <Footer />
            </>
        );
    }

    if (error) {
        return (
            <>
                <Header />
                <div className="main main-app p-3 p-lg-4" style={{ background: '#f8fafc', minHeight: '100vh' }}>
                    <Alert className="alert-error-charte">
                        <div className="d-flex align-items-center">
                            <div className="alert-error-icon"><i className="ri-error-warning-line"></i></div>
                            <div className="ms-3 flex-grow-1">
                                <h6 className="mb-1 fw-bold">Erreur de chargement</h6>
                                <p className="mb-0 small">{error}</p>
                            </div>
                            <Button onClick={handleRefresh} className="btn-modern-outline">
                                <i className="ri-refresh-line me-1"></i>Réessayer
                            </Button>
                        </div>
                    </Alert>
                </div>
                <Footer />
            </>
        );
    }

    const today = new Date().toISOString().split('T')[0];
    const isToday = selectedDate === today;

    /* ============================================================
       RENDU PRINCIPAL
       ============================================================ */
    return (
        <>
            <Header />
            <div className="main main-app p-3 p-lg-4" style={{ background: '#f8fafc', minHeight: '100vh' }}>
                <div className="container-fluid">

                    {/* ===== EN-TÊTE MODERNE ===== */}
                    <div className="header-modern mb-4">
                        <div className="header-left">
                            <div className="header-icon-wrapper">
                                <i className="ri-shopping-bag-3-line"></i>
                            </div>
                            <div>
                                <h5 className="header-title">
                                    {isToday ? "Ventes d'aujourd'hui" : `Ventes du ${formatDate(selectedDate)}`}
                                </h5>
                                <div className="d-flex flex-wrap gap-2 mt-1">
                                    <span className="badge-modern">
                                        <i className="ri-money-euro-circle-line"></i> CA {formatCurrency(data?.resume?.total_ttc || 0)}
                                    </span>
                                    <span className="badge-modern" style={{ background: `${COLORS.success}15`, color: COLORS.success }}>
                                        <i className="ri-file-list-3-line"></i> {data?.resume?.nb_factures || 0} factures
                                    </span>
                                    <span className="badge-modern" style={{ background: `${COLORS.info}15`, color: COLORS.info }}>
                                        <i className="ri-group-line"></i> {data?.resume?.nb_clients || 0} clients
                                    </span>
                                </div>
                            </div>
                        </div>
                        <div className="d-flex gap-2 flex-wrap">
                            <div className="date-picker-charte">
                                <i className="ri-calendar-line date-picker-icon-charte"></i>
                                <Form.Control
                                    type="date"
                                    value={selectedDate}
                                    onChange={handleDateChange}
                                    className="date-picker-input-charte"
                                    max={today}
                                />
                            </div>
                            <Button className="btn-modern-primary" onClick={handleRefresh} disabled={loading}>
                                <i className={`ri-refresh-line ${loading ? 'spin-icon' : ''}`}></i> Actualiser
                            </Button>
                        </div>
                    </div>

                    {/* ===== KPI CARDS ===== */}
                    <Row className="g-3 mb-4">
                        <Col sm="6" xl="3">
                            <div className="kpi-charte kpi-primary">
                                <div className="kpi-icon-charte"><i className="ri-money-euro-circle-line"></i></div>
                                <div className="kpi-content-charte">
                                    <span className="kpi-label-charte">Chiffre d'affaires</span>
                                    <h3 className="kpi-value-charte">{formatCurrency(data?.resume?.total_ttc || 0)}</h3>
                                    <span className="kpi-unit-charte">{Math.abs(data?.comparaison?.evolution || 0)}% vs hier</span>
                                </div>
                            </div>
                        </Col>
                        <Col sm="6" xl="3">
                            <div className="kpi-charte kpi-success">
                                <div className="kpi-icon-charte"><i className="ri-file-list-3-line"></i></div>
                                <div className="kpi-content-charte">
                                    <span className="kpi-label-charte">Factures</span>
                                    <h3 className="kpi-value-charte">{data?.resume?.nb_factures || 0}</h3>
                                    <span className="kpi-unit-charte">{Math.abs(data?.comparaison?.evolution_factures || 0)}% vs hier</span>
                                </div>
                            </div>
                        </Col>
                        <Col sm="6" xl="3">
                            <div className="kpi-charte kpi-warning">
                                <div className="kpi-icon-charte"><i className="ri-group-line"></i></div>
                                <div className="kpi-content-charte">
                                    <span className="kpi-label-charte">Clients servis</span>
                                    <h3 className="kpi-value-charte">{data?.resume?.nb_clients || 0}</h3>
                                    <span className="kpi-unit-charte">
                                        Panier moyen: {formatCurrency(data?.resume?.nb_factures > 0 ? data?.resume?.total_ttc / data?.resume?.nb_factures : 0)}
                                    </span>
                                </div>
                            </div>
                        </Col>
                        <Col sm="6" xl="3">
                            <div className="kpi-charte kpi-info">
                                <div className="kpi-icon-charte"><i className="ri-stack-line"></i></div>
                                <div className="kpi-content-charte">
                                    <span className="kpi-label-charte">Produits vendus</span>
                                    <h3 className="kpi-value-charte">{data?.resume?.quantite_totale || 0}</h3>
                                    <span className="kpi-unit-charte">{data?.resume?.nb_produits || 0} références</span>
                                </div>
                            </div>
                        </Col>
                    </Row>

                    {/* ===== GRAPHIQUES ===== */}
                    <Row className="g-3 mb-4">
                        <Col xl="8">
                            <Card className="border-0 shadow-sm rounded-3 h-100">
                                <Card.Header className="card-header-gradient">
                                    <div className="d-flex align-items-center justify-content-between">
                                        <div className="d-flex align-items-center gap-2">
                                            <i className="ri-bar-chart-line text-white"></i>
                                            <h6 className="mb-0 fw-semibold text-white">Ventes par heure</h6>
                                        </div>
                                        <span className="badge-header-light">
                                            Total: {formatCurrency(data?.ventes_par_heure?.reduce((acc, item) => acc + item.montant_total, 0) || 0)}
                                        </span>
                                    </div>
                                </Card.Header>
                                <Card.Body className="p-3">
                                    <ReactApexChart series={seriesVentesHeure} options={optionsVentesHeure} type="area" height={350} />
                                </Card.Body>
                            </Card>
                        </Col>
                        <Col xl="4">
                            <Card className="border-0 shadow-sm rounded-3 h-100">
                                <Card.Header className="card-header-gradient-secondary">
                                    <div className="d-flex align-items-center gap-2">
                                        <i className="ri-pie-chart-2-line text-white"></i>
                                        <h6 className="mb-0 fw-semibold text-white">Répartition des paiements</h6>
                                    </div>
                                </Card.Header>
                                <Card.Body className="p-3">
                                    {data?.moyens_paiement?.length > 0 ? (
                                        <>
                                            <ReactApexChart series={seriesRepartitionPaiements} options={donutOptions} type="donut" height={260} />
                                            <div className="mt-3">
                                                {data?.moyens_paiement?.map((paiement, index) => {
                                                    const colors = [COLORS.primary, COLORS.cyan || '#06b6d4', COLORS.success, COLORS.warning, COLORS.danger];
                                                    return (
                                                        <div key={index} className="mode-paiement-row-charte">
                                                            <div className="d-flex align-items-center gap-2">
                                                                <div className="mode-paiement-icon-charte" style={{ background: `${colors[index % colors.length]}15`, color: colors[index % colors.length] }}>
                                                                    <i className="ri-bank-card-line"></i>
                                                                </div>
                                                                <span className="mode-paiement-label-charte">
                                                                    {getModePaiementLabel(paiement.mode_reglement)}
                                                                </span>
                                                            </div>
                                                            <div className="d-flex align-items-center gap-2">
                                                                <span className="mode-paiement-value-charte" style={{ color: colors[index % colors.length] }}>
                                                                    {formatCurrency(paiement.total)}
                                                                </span>
                                                                <span className="badge-count-charte">{paiement.nb_paiements}</span>
                                                            </div>
                                                        </div>
                                                    );
                                                })}
                                            </div>
                                        </>
                                    ) : (
                                        <div className="empty-state-charte">
                                            <div className="empty-state-icon-charte" style={{ background: `linear-gradient(135deg, ${COLORS.secondary}15, ${COLORS.secondary}05)`, color: COLORS.secondary }}>
                                                <i className="ri-pie-chart-2-line"></i>
                                            </div>
                                            <h6 className="fw-semibold mb-1">Aucun paiement</h6>
                                            <p className="text-muted small mb-0">Aucun paiement enregistré</p>
                                        </div>
                                    )}
                                </Card.Body>
                            </Card>
                        </Col>
                    </Row>

                    {/* ===== TABS ===== */}
                    <Card className="border-0 shadow-sm rounded-3 overflow-hidden">
                        <Card.Header className="card-header-gradient">
                            <div className="tabs-charte">
                                {[
                                    { id: 'factures', label: 'Factures', icon: 'ri-file-list-3-line', count: data?.factures?.length || 0 },
                                    { id: 'produits', label: 'Produits', icon: 'ri-suitcase-2-line', count: data?.produits?.length || 0 },
                                    { id: 'paiements', label: 'Paiements', icon: 'ri-bank-card-line', count: data?.paiements?.length || 0 },
                                    { id: 'vendeurs', label: 'Performance vendeurs', icon: 'ri-user-star-line', count: 0 }
                                ].map((tab) => (
                                    <button
                                        key={tab.id}
                                        className={`tab-btn-charte ${activeTab === tab.id ? 'active' : ''}`}
                                        onClick={() => setActiveTab(tab.id)}
                                    >
                                        <i className={tab.icon}></i>
                                        <span>{tab.label}</span>
                                        {tab.count > 0 && (
                                            <span className="tab-count-charte">{tab.count}</span>
                                        )}
                                    </button>
                                ))}
                            </div>
                        </Card.Header>
                        <Card.Body className="p-0">

                            {/* ===== TAB FACTURES ===== */}
                            {activeTab === 'factures' && (
                                <div className="table-responsive">
                                    <Table hover className="align-middle mb-0 table-charte">
                                        <thead>
                                            <tr>
                                                <th className="ps-4">N° Facture</th>
                                                <th>Heure</th>
                                                <th>Client</th>
                                                <th className="text-end">HT</th>
                                                <th className="text-end">TVA</th>
                                                <th className="text-end">TTC</th>
                                                <th className="text-end">Réglé</th>
                                                <th className="text-end">Reste</th>
                                                <th className="text-center">Statut</th>
                                                <th>Vendeur</th>
                                                <th className="text-center pe-4"></th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {data?.factures?.map((facture) => (
                                                <tr key={facture.id_facture}>
                                                    <td className="ps-4">
                                                        <Link to={`/ventes/factures/${facture.id_facture}`} className="table-link-charte">
                                                            {facture.numero_facture}
                                                        </Link>
                                                    </td>
                                                    <td className="text-muted" style={{ fontSize: '0.8rem' }}>
                                                        <i className="ri-time-line me-1"></i>
                                                        {new Date(facture.date_facture).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}
                                                    </td>
                                                    <td>
                                                        <div className="fw-semibold" style={{ color: COLORS.primaryDark, fontSize: '0.85rem' }}>{facture.raison_sociale}</div>
                                                        <small className="text-muted">{facture.code_client}</small>
                                                    </td>
                                                    <td className="text-end">{formatCurrency(facture.montant_ht)}</td>
                                                    <td className="text-end" style={{ color: COLORS.warning }}>{formatCurrency(facture.montant_tva)}</td>
                                                    <td className="text-end fw-bold" style={{ color: COLORS.primaryDark }}>{formatCurrency(facture.montant_ttc)}</td>
                                                    <td className="text-end" style={{ color: COLORS.success }}>{formatCurrency(facture.montant_regle)}</td>
                                                    <td className="text-end" style={{ color: COLORS.danger }}>{formatCurrency(facture.montant_ttc - facture.montant_regle)}</td>
                                                    <td className="text-center">{getStatusBadge(facture.statut)}</td>
                                                    <td>
                                                        <small style={{ fontSize: '0.75rem', color: '#64748b' }}>
                                                            <i className="ri-user-line me-1"></i>
                                                            {facture.prenom_utilisateur} {facture.nom_utilisateur}
                                                        </small>
                                                    </td>
                                                    <td className="text-center pe-4">
                                                        <button className="action-btn-charte" title="Imprimer">
                                                            <i className="ri-printer-line"></i>
                                                        </button>
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                        <tfoot>
                                            <tr>
                                                <td colSpan="5" className="text-end fw-bold ps-4">TOTAUX :</td>
                                                <td className="text-end fw-bold" style={{ color: COLORS.primaryDark }}>{formatCurrency(data?.resume?.total_ttc || 0)}</td>
                                                <td className="text-end fw-bold" style={{ color: COLORS.success }}>{formatCurrency(data?.resume?.total_regle || 0)}</td>
                                                <td className="text-end fw-bold" style={{ color: COLORS.danger }}>{formatCurrency(data?.resume?.reste_a_payer || 0)}</td>
                                                <td colSpan="3"></td>
                                            </tr>
                                        </tfoot>
                                    </Table>
                                </div>
                            )}

                            {/* ===== TAB PRODUITS ===== */}
                            {activeTab === 'produits' && (
                                <div className="table-responsive">
                                    <Table hover className="align-middle mb-0 table-charte">
                                        <thead>
                                            <tr>
                                                <th className="ps-4">Référence</th>
                                                <th>Produit</th>
                                                <th>Catégorie</th>
                                                <th className="text-end">Qté</th>
                                                <th className="text-end">Prix unitaire</th>
                                                <th className="text-end">Total HT</th>
                                                <th className="text-end pe-4">% des ventes</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {data?.produits?.map((produit) => {
                                                const pourcentage = (produit.total_ligne_ht / (data?.resume?.total_ht || 1)) * 100;
                                                return (
                                                    <tr key={produit.id_produit}>
                                                        <td className="ps-4">
                                                            <span className="reference-badge-charte">
                                                                <i className="ri-barcode-line"></i>{produit.reference}
                                                            </span>
                                                        </td>
                                                        <td className="fw-semibold" style={{ color: COLORS.primaryDark }}>{produit.designation}</td>
                                                        <td className="text-muted" style={{ fontSize: '0.8rem' }}>{produit.nom_categorie || '—'}</td>
                                                        <td className="text-end fw-bold" style={{ color: COLORS.primaryDark }}>{formatNumber(produit.quantite_vendue)}</td>
                                                        <td className="text-end">{formatCurrency(produit.prix_unitaire_ht)}</td>
                                                        <td className="text-end fw-bold" style={{ color: COLORS.primary }}>{formatCurrency(produit.total_ligne_ht)}</td>
                                                        <td className="text-end pe-4">
                                                            <div className="d-flex align-items-center justify-content-end gap-2">
                                                                <div className="progress-charte" style={{ width: '60px' }}>
                                                                    <div className="progress-bar-charte" style={{ width: `${Math.min(pourcentage, 100)}%`, background: `linear-gradient(90deg, ${COLORS.success}, #059669)` }}></div>
                                                                </div>
                                                                <small className="text-muted" style={{ fontSize: '0.7rem' }}>{pourcentage.toFixed(1)}%</small>
                                                            </div>
                                                        </td>
                                                    </tr>
                                                );
                                            })}
                                        </tbody>
                                    </Table>
                                </div>
                            )}

                            {/* ===== TAB PAIEMENTS ===== */}
                            {activeTab === 'paiements' && (
                                <div className="table-responsive">
                                    <Table hover className="align-middle mb-0 table-charte">
                                        <thead>
                                            <tr>
                                                <th className="ps-4">N° Opération</th>
                                                <th>Heure</th>
                                                <th>Client</th>
                                                <th>Facture</th>
                                                <th>Mode</th>
                                                <th>Référence</th>
                                                <th className="text-end">Montant</th>
                                                <th className="text-center pe-4">Statut</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {data?.paiements?.map((paiement) => (
                                                <tr key={paiement.id_reglement}>
                                                    <td className="ps-4">
                                                        <span className="reference-badge-charte">
                                                            <i className="ri-hashtag"></i>{paiement.numero_reglement}
                                                        </span>
                                                    </td>
                                                    <td className="text-muted" style={{ fontSize: '0.8rem' }}>
                                                        <i className="ri-time-line me-1"></i>
                                                        {new Date(paiement.date_reglement).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}
                                                    </td>
                                                    <td className="fw-semibold" style={{ color: COLORS.primaryDark, fontSize: '0.85rem' }}>{paiement.client}</td>
                                                    <td style={{ fontSize: '0.85rem' }}>{paiement.numero_facture}</td>
                                                    <td>
                                                        <span className="mode-badge-charte">
                                                            <i className="ri-bank-card-line"></i>
                                                            {getModePaiementLabel(paiement.mode_reglement)}
                                                        </span>
                                                    </td>
                                                    <td className="text-muted small">{paiement.reference || '—'}</td>
                                                    <td className="text-end fw-bold" style={{ color: COLORS.success }}>{formatCurrency(paiement.montant)}</td>
                                                    <td className="text-center pe-4">
                                                        <span className="status-badge-charte" style={{
                                                            background: paiement.statut === 'valide'
                                                                ? `linear-gradient(135deg, ${COLORS.success}, #059669)`
                                                                : `linear-gradient(135deg, ${COLORS.warning}, #d97706)`
                                                        }}>
                                                            <i className={paiement.statut === 'valide' ? 'ri-check-line' : 'ri-time-line'}></i>
                                                            {paiement.statut === 'valide' ? 'Validé' : 'En attente'}
                                                        </span>
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                        <tfoot>
                                            <tr>
                                                <td colSpan="6" className="text-end fw-bold ps-4">TOTAL :</td>
                                                <td className="text-end fw-bold" style={{ color: COLORS.success }}>
                                                    {formatCurrency(data?.paiements?.reduce((acc, p) => acc + p.montant, 0) || 0)}
                                                </td>
                                                <td className="pe-4"></td>
                                            </tr>
                                        </tfoot>
                                    </Table>
                                </div>
                            )}

                            {/* ===== TAB VENDEURS ===== */}
                            {activeTab === 'vendeurs' && (
                                <div className="p-4">
                                    <Row className="g-3">
                                        {data?.top_vendeurs?.map((vendeur, index) => {
                                            const pourcentage = (vendeur.total_ventes / (data?.resume?.total_ttc || 1)) * 100;
                                            const gradientColors = [
                                                `linear-gradient(135deg, ${COLORS.primary}, ${COLORS.primaryDark})`,
                                                `linear-gradient(135deg, ${COLORS.success}, #059669)`,
                                                `linear-gradient(135deg, ${COLORS.warning}, #d97706)`
                                            ];
                                            return (
                                                <Col md="4" key={vendeur.id_utilisateur}>
                                                    <div className="vendeur-card-charte">
                                                        <div className="d-flex align-items-center mb-3">
                                                            <div className="vendeur-rank-charte" style={{ background: gradientColors[index % 3] }}>
                                                                #{index + 1}
                                                            </div>
                                                            <div>
                                                                <h6 className="mb-0 fw-semibold" style={{ color: COLORS.primaryDark, fontSize: '0.9rem' }}>
                                                                    {vendeur.prenom_utilisateur} {vendeur.nom_utilisateur}
                                                                </h6>
                                                                <small className="text-muted">{vendeur.nb_ventes} ventes</small>
                                                            </div>
                                                        </div>
                                                        <div className="d-flex justify-content-between align-items-center mb-2">
                                                            <span className="text-muted small">CA réalisé</span>
                                                            <span className="fw-bold" style={{ color: COLORS.primary }}>
                                                                {formatCurrency(vendeur.total_ventes)}
                                                            </span>
                                                        </div>
                                                        <div className="progress-charte">
                                                            <div className="progress-bar-charte" style={{ width: `${Math.min(pourcentage, 100)}%`, background: `linear-gradient(90deg, ${COLORS.success}, #059669)` }}></div>
                                                        </div>
                                                        <small className="text-muted d-block text-end mt-1" style={{ fontSize: '0.7rem' }}>
                                                            {pourcentage.toFixed(1)}% du total
                                                        </small>
                                                    </div>
                                                </Col>
                                            );
                                        })}
                                    </Row>
                                </div>
                            )}
                        </Card.Body>
                    </Card>
                </div>
                <Footer />
            </div>

            {/* ===== STYLES CHARTE ===== */}
            <style>{`
                :root {
                    --primary: ${COLORS.primary || '#295AA7'};
                    --primary-dark: ${COLORS.primaryDark || '#1e3f7a'};
                    --secondary: ${COLORS.secondary || '#21ACCD'};
                    --success: ${COLORS.success || '#10b981'};
                    --danger: ${COLORS.danger || '#ef4444'};
                    --warning: ${COLORS.warning || '#f59e0b'};
                    --info: ${COLORS.info || '#3b82f6'};
                }

                /* ===== EN-TÊTE ===== */
                .header-modern {
                    background: white; padding: 1.2rem 1.8rem;
                    box-shadow: 0 2px 8px rgba(0,0,0,0.04);
                    border-left: 4px solid var(--secondary);
                    border-radius: 12px;
                    display: flex; align-items: center; justify-content: space-between;
                    flex-wrap: wrap; gap: 1rem;
                }
                .header-left { display: flex; align-items: center; gap: 1rem; }
                .header-icon-wrapper {
                    width: 52px; height: 52px;
                    background: linear-gradient(135deg, var(--primary), var(--secondary));
                    display: flex; align-items: center; justify-content: center;
                    border-radius: 14px; color: white; font-size: 1.5rem;
                    box-shadow: 0 4px 12px rgba(41, 90, 167, 0.25);
                }
                .header-title { color: #1e293b; font-size: 1.3rem; font-weight: 700; margin: 0; }
                .badge-modern {
                    background: #f1f4f9; color: #475569;
                    padding: 0.3rem 0.8rem; border-radius: 50px;
                    font-size: 0.75rem; font-weight: 600;
                    display: inline-flex; align-items: center; gap: 6px;
                }
                .badge-modern i { color: var(--secondary); }
                .spin-icon { animation: spin 1s linear infinite; }

                /* ===== DATE PICKER ===== */
                .date-picker-charte {
                    position: relative;
                    display: inline-flex;
                    align-items: center;
                }
                .date-picker-icon-charte {
                    position: absolute;
                    left: 1rem;
                    color: var(--primary);
                    pointer-events: none;
                    z-index: 2;
                }
                .date-picker-input-charte {
                    padding: 0.5rem 1rem 0.5rem 2.75rem !important;
                    border: 1.5px solid #d1d5db !important;
                    border-radius: 50px !important;
                    background: white !important;
                    font-size: 0.85rem;
                    min-width: 180px;
                    transition: all 0.2s ease;
                }
                .date-picker-input-charte:focus {
                    border-color: var(--secondary) !important;
                    box-shadow: 0 0 0 3px rgba(33,172,205,0.15) !important;
                }

                /* ===== KPI ===== */
                .kpi-charte {
                    background: white; border-radius: 12px;
                    padding: 1.2rem;
                    display: flex; align-items: center; gap: 1rem;
                    box-shadow: 0 2px 8px rgba(0,0,0,0.04);
                    border-left: 4px solid;
                    transition: all 0.25s ease;
                    height: 100%;
                }
                .kpi-charte:hover {
                    transform: translateY(-3px);
                    box-shadow: 0 12px 28px rgba(0,0,0,0.08);
                }
                .kpi-primary { border-left-color: var(--primary); }
                .kpi-success { border-left-color: var(--success); }
                .kpi-warning { border-left-color: var(--warning); }
                .kpi-info    { border-left-color: var(--info); }
                .kpi-icon-charte {
                    width: 48px; height: 48px; border-radius: 12px;
                    display: flex; align-items: center; justify-content: center;
                    font-size: 1.4rem; flex-shrink: 0;
                }
                .kpi-primary .kpi-icon-charte { background: ${COLORS.primary}15; color: var(--primary); }
                .kpi-success .kpi-icon-charte { background: ${COLORS.success}15; color: var(--success); }
                .kpi-warning .kpi-icon-charte { background: ${COLORS.warning}15; color: var(--warning); }
                .kpi-info    .kpi-icon-charte { background: ${COLORS.info}15; color: var(--info); }
                .kpi-content-charte { flex: 1; min-width: 0; }
                .kpi-label-charte {
                    font-size: 0.7rem; color: #94a3b8;
                    text-transform: uppercase; font-weight: 600;
                    letter-spacing: 0.5px;
                }
                .kpi-value-charte {
                    font-size: 1.35rem; font-weight: 700;
                    margin: 0.3rem 0; color: #1e293b;
                }
                .kpi-unit-charte { font-size: 0.7rem; color: #94a3b8; }

                /* ===== HEADERS GRADIENT ===== */
                .card-header-gradient {
                    background: linear-gradient(135deg, var(--primary) 0%, var(--primary-dark) 100%);
                    border-top-left-radius: 12px !important;
                    border-top-right-radius: 12px !important;
                    padding: 1rem 1.5rem;
                    border: none;
                }
                .card-header-gradient-secondary {
                    background: linear-gradient(135deg, var(--secondary) 0%, #0d8ba6 100%);
                    border-top-left-radius: 12px !important;
                    border-top-right-radius: 12px !important;
                    padding: 1rem 1.5rem;
                    border: none;
                }
                .badge-header-light {
                    background: rgba(255,255,255,0.2);
                    color: white;
                    border: 1px solid rgba(255,255,255,0.3);
                    border-radius: 50px;
                    padding: 0.25rem 0.75rem;
                    font-size: 0.75rem;
                    font-weight: 600;
                }

                /* ===== TABS ===== */
                .tabs-charte {
                    display: flex;
                    gap: 0.25rem;
                    flex-wrap: wrap;
                }
                .tab-btn-charte {
                    border: none;
                    background: transparent;
                    color: rgba(255,255,255,0.7);
                    padding: 0.6rem 1rem;
                    border-radius: 10px;
                    font-size: 0.85rem;
                    font-weight: 500;
                    cursor: pointer;
                    transition: all 0.2s ease;
                    display: inline-flex;
                    align-items: center;
                    gap: 8px;
                }
                .tab-btn-charte:hover {
                    background: rgba(255,255,255,0.1);
                    color: white;
                }
                .tab-btn-charte.active {
                    background: white;
                    color: var(--primary);
                    font-weight: 600;
                    box-shadow: 0 4px 12px rgba(0,0,0,0.15);
                }
                .tab-count-charte {
                    background: ${COLORS.primary}20;
                    color: var(--primary);
                    border-radius: 50px;
                    padding: 0.1rem 0.5rem;
                    font-size: 0.65rem;
                    font-weight: 700;
                    min-width: 20px;
                    text-align: center;
                }
                .tab-btn-charte.active .tab-count-charte {
                    background: var(--primary);
                    color: white;
                }

                /* ===== TABLE ===== */
                .table-charte thead th {
                    background: #f8fafc; color: #475569;
                    font-weight: 600; font-size: 0.7rem;
                    text-transform: uppercase; letter-spacing: 0.5px;
                    border-bottom: 2px solid #e2e8f0;
                    padding: 0.9rem 0.75rem;
                }
                .table-charte tbody td {
                    padding: 0.9rem 0.75rem;
                    vertical-align: middle;
                    border-bottom: 1px solid #f1f5f9;
                    font-size: 0.85rem;
                }
                .table-charte tbody tr { transition: background 0.15s ease; }
                .table-charte tbody tr:hover { background: #f8fafc; }
                .table-charte tfoot td {
                    background: #f8fafc;
                    padding: 0.85rem 0.75rem;
                    border-top: 2px solid #e2e8f0;
                    font-size: 0.85rem;
                }
                .table-link-charte {
                    color: var(--primary); text-decoration: none;
                    font-weight: 600;
                    transition: color 0.2s;
                }
                .table-link-charte:hover {
                    color: var(--primary-dark);
                    text-decoration: underline;
                }
                .reference-badge-charte {
                    display: inline-flex; align-items: center; gap: 4px;
                    background: #f1f5f9; padding: 0.25rem 0.6rem;
                    border-radius: 6px; font-size: 0.7rem;
                    font-family: monospace; color: #475569;
                }
                .status-badge-charte {
                    display: inline-flex; align-items: center; gap: 4px;
                    color: white; padding: 0.3rem 0.75rem;
                    border-radius: 50px; font-size: 0.7rem; font-weight: 600;
                    white-space: nowrap;
                }
                .mode-badge-charte {
                    display: inline-flex; align-items: center; gap: 4px;
                    background: #f1f5f9; color: #475569;
                    padding: 0.25rem 0.65rem;
                    border-radius: 50px; font-size: 0.7rem; font-weight: 500;
                }

                /* ===== ACTION BTN ===== */
                .action-btn-charte {
                    width: 32px; height: 32px; border-radius: 8px;
                    display: inline-flex; align-items: center; justify-content: center;
                    background: white; border: 1.5px solid #e2e8f0;
                    color: #64748b; font-size: 0.9rem;
                    transition: all 0.2s ease;
                    cursor: pointer;
                }
                .action-btn-charte:hover {
                    border-color: var(--primary); color: var(--primary);
                    transform: translateY(-2px);
                    box-shadow: 0 6px 16px rgba(41, 90, 167, 0.15);
                }

                /* ===== MODE PAIEMENT ROW ===== */
                .mode-paiement-row-charte {
                    display: flex; justify-content: space-between;
                    align-items: center;
                    padding: 0.6rem 0;
                    border-bottom: 1px dashed #e2e8f0;
                }
                .mode-paiement-row-charte:last-child { border-bottom: none; }
                .mode-paiement-icon-charte {
                    width: 30px; height: 30px; border-radius: 8px;
                    display: flex; align-items: center; justify-content: center;
                    font-size: 0.9rem;
                }
                .mode-paiement-label-charte {
                    font-size: 0.8rem; font-weight: 600;
                    color: var(--primary-dark);
                }
                .mode-paiement-value-charte {
                    font-size: 0.85rem; font-weight: 700;
                }
                .badge-count-charte {
                    background: #f1f5f9; color: #64748b;
                    border-radius: 50px; padding: 0.1rem 0.5rem;
                    font-size: 0.65rem; font-weight: 700;
                }

                /* ===== PROGRESS ===== */
                .progress-charte {
                    height: 6px; border-radius: 4px;
                    background: #f1f5f9;
                    overflow: hidden;
                }
                .progress-bar-charte {
                    height: 100%; border-radius: 4px;
                    transition: width 0.6s ease;
                }

                /* ===== VENDEUR CARD ===== */
                .vendeur-card-charte {
                    background: white;
                    border-radius: 12px;
                    padding: 1.25rem;
                    box-shadow: 0 2px 8px rgba(0,0,0,0.04);
                    transition: all 0.3s ease;
                    height: 100%;
                }
                .vendeur-card-charte:hover {
                    transform: translateY(-4px);
                    box-shadow: 0 12px 28px rgba(0,0,0,0.08);
                }
                .vendeur-rank-charte {
                    width: 48px; height: 48px; border-radius: 50%;
                    display: flex; align-items: center; justify-content: center;
                    color: white; font-weight: 700;
                    font-size: 1rem;
                    margin-right: 1rem;
                    flex-shrink: 0;
                    box-shadow: 0 4px 12px rgba(41,90,167,0.25);
                }

                /* ===== EMPTY STATE ===== */
                .empty-state-charte { text-align: center; padding: 3rem 2rem; }
                .empty-state-icon-charte {
                    width: 80px; height: 80px;
                    border-radius: 50%;
                    display: flex; align-items: center; justify-content: center;
                    margin: 0 auto 1rem; font-size: 2rem;
                }

                /* ===== ALERTE ERREUR ===== */
                .alert-error-charte {
                    border: none; background: #fef2f2;
                    border-left: 4px solid var(--danger);
                    border-radius: 16px; padding: 1rem 1.5rem;
                    box-shadow: 0 8px 32px rgba(239,68,68,0.08);
                }
                .alert-error-icon {
                    width: 48px; height: 48px; border-radius: 12px;
                    background: rgba(239,68,68,0.1);
                    display: flex; align-items: center; justify-content: center;
                    color: var(--danger); font-size: 1.5rem;
                }

                /* ===== BOUTONS ===== */
                .btn-modern-outline {
                    border: 1.5px solid #d1d5db; background: white;
                    padding: 0.5rem 1.4rem; border-radius: 50px;
                    font-size: 0.85rem; font-weight: 500; color: #4b5563;
                    transition: all 0.2s ease;
                    display: inline-flex; align-items: center; gap: 6px;
                }
                .btn-modern-outline:hover {
                    border-color: var(--secondary); color: var(--secondary);
                    background: #f0fbff; transform: translateY(-1px);
                    box-shadow: 0 4px 8px rgba(33,172,205,0.15);
                }
                .btn-modern-primary {
                    background: linear-gradient(135deg, var(--primary), var(--primary-dark));
                    border: none; color: white; padding: 0.5rem 1.6rem;
                    border-radius: 50px; font-weight: 600; font-size: 0.85rem;
                    transition: all 0.2s ease;
                    box-shadow: 0 4px 12px rgba(41,90,167,0.3);
                    display: inline-flex; align-items: center; gap: 6px;
                }
                .btn-modern-primary:hover {
                    transform: translateY(-2px);
                    box-shadow: 0 6px 18px rgba(41,90,167,0.4); color: white;
                }
                .btn-modern-primary:disabled { opacity: 0.6; transform: none; }

                /* ===== LOADING ===== */
                .loading-spinner-charte { position: relative; width: 80px; height: 80px; margin: 0 auto; }
                .loading-ring-charte {
                    position: absolute; inset: 0; border-radius: 50%;
                    border: 4px solid #e2e8f0; border-top-color: var(--primary);
                    animation: spin 1s linear infinite;
                }
                .loading-icon-charte {
                    position: absolute; top: 50%; left: 50%;
                    transform: translate(-50%, -50%);
                    font-size: 28px; color: var(--primary);
                }

                /* ===== ANIMATIONS ===== */
                @keyframes spin { to { transform: rotate(360deg); } }

                /* ===== RESPONSIVE ===== */
                @media (max-width: 768px) {
                    .header-modern { padding: 1rem; }
                    .header-icon-wrapper { width: 44px; height: 44px; font-size: 1.2rem; }
                    .header-title { font-size: 1.1rem; }
                    .date-picker-input-charte { min-width: 100%; width: 100%; }
                    .tab-btn-charte { font-size: 0.7rem; padding: 0.5rem 0.6rem; }
                    .vendeur-rank-charte { width: 40px; height: 40px; font-size: 0.9rem; }
                }
                .rounded-3 { border-radius: 12px !important; }
            `}</style>
        </>
    );
}